import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, LogOut, ArrowRight, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { TERMS_CURRENT_VERSION, TERMS_VERSION_LABEL } from "@/config/legalText";

/**
 * Gate que bloquea el uso de la app hasta que el usuario acepte la versión
 * vigente de Términos y Aviso de Privacidad. Se aplica DESPUÉS del auth
 * check pero ANTES de renderizar contenido protegido.
 *
 * Regla: si `user.terms_version_accepted !== TERMS_CURRENT_VERSION`, se
 * muestra el modal bloqueante. Al aceptar, se llama POST /auth/accept-terms
 * y se recarga el estado del usuario. Aplica a los 3 tipos de cuenta
 * (asesor, inmobiliaria madre y subusuario) — sin excepción.
 */
export default function TermsAcceptanceGate({ children }) {
  const { user, refresh, logout } = useAuth();
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const needsAcceptance = user && user.terms_version_accepted !== TERMS_CURRENT_VERSION;
  if (!needsAcceptance) return children;

  // En mobile abrimos popup inline; en desktop nueva pestaña. Detectamos ancho
  // en el momento del click (respeta rotación / responsive).
  const openLegal = (path) => (e) => {
    e.preventDefault();
    if (window.matchMedia("(max-width: 767px)").matches) {
      window.location.href = path;
    } else {
      window.open(path, "_blank", "noopener,noreferrer");
    }
  };

  const submit = async () => {
    if (!checked) return;
    setSubmitting(true);
    try {
      await api.post("/auth/accept-terms", { version: TERMS_CURRENT_VERSION });
      await refresh();
      toast.success("Términos aceptados. ¡Listo!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo registrar la aceptación");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="terms-gate-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-2">
          <AlertTriangle className="w-6 h-6 text-[#D99B26]" />
          <h2 className="text-2xl font-heading font-bold text-slate-900">
            Actualizamos nuestros términos
          </h2>
        </div>
        <p className="text-sm text-slate-600 mb-5">
          Antes de continuar necesitamos que confirmes que aceptas la nueva versión ({TERMS_VERSION_LABEL}).
          Esto aplica a todos los tipos de cuenta, incluidas las cuentas ya existentes.
        </p>

        <div className="rounded-xl border border-[#FCD34D] bg-[#FEF3C7] text-[#92400E] p-3 text-xs mb-5">
          Versión piloto: el contenido está en fase de validación jurídica y será reemplazado por la
          v2.0 antes del lanzamiento comercial.
        </div>

        <label className="flex items-start gap-3 cursor-pointer mb-5" data-testid="terms-gate-checkbox-label">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            data-testid="terms-gate-checkbox"
            className="mt-1 w-5 h-5 rounded border-[#CBD5E1] text-[#1E3A5F] focus:ring-[#1E3A5F]"
          />
          <span className="text-sm text-slate-700">
            He leído y acepto el{" "}
            <a href="/aviso-privacidad" onClick={openLegal("/aviso-privacidad")}
               className="text-[#1E3A5F] font-semibold underline inline-flex items-center gap-1"
               data-testid="terms-gate-link-privacy">
              Aviso de Privacidad
              <ExternalLink className="w-3 h-3" />
            </a>
            {" "}y los{" "}
            <a href="/terminos" onClick={openLegal("/terminos")}
               className="text-[#1E3A5F] font-semibold underline inline-flex items-center gap-1"
               data-testid="terms-gate-link-terms">
              Términos de Uso
              <ExternalLink className="w-3 h-3" />
            </a>
            {" "}({TERMS_VERSION_LABEL}).
          </span>
        </label>

        <button
          type="button"
          onClick={submit}
          disabled={!checked || submitting}
          data-testid="terms-gate-accept-btn"
          className="bm-btn-primary w-full disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {submitting ? "Guardando…" : "Aceptar y continuar"}
          <ArrowRight className="w-4 h-4" />
        </button>

        <button
          type="button"
          onClick={logout}
          data-testid="terms-gate-logout-btn"
          className="mt-3 w-full text-sm text-slate-500 hover:text-slate-800 flex items-center justify-center gap-1.5"
        >
          <LogOut className="w-4 h-4" /> Cerrar sesión
        </button>

        <p className="mt-4 text-[11px] text-slate-400 text-center">
          Registramos fecha, hora y versión aceptada en tu perfil.
        </p>
      </div>
    </div>
  );
}
